import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Do you even need that bind?",
  "description": "",
  "published": "2016-11-29T00:00:00.000Z",
  "redirect_from": ["/blog/do-you-even-need-that-bind/swizec/7247"],
  "categories": "Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><strong parentName="p">{`Codereviewer:`}</strong>{` `}<em parentName="p">{`"Why is this binding necessary?"`}</em></p>
    <pre><code parentName="pre" {...{}}>{`<Modal show={viewStore.modal.show}
       closeOnOuterClick={true}
       onClose={viewStore.closeModal.bind(viewStore)}
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`This code follows my `}<a parentName="p" {...{
          "href": "https://swizec.com/blog/simple-mobx-driven-modals/swizec/7166"
        }}>{`Simple MobX-driven modals`}</a>{` approach.`}</p>
    </blockquote>
    <p><strong parentName="p">{`Swiz:`}</strong>{` `}<em parentName="p">{`"Without binding, the callback would bind to Modal object when called, if I'm not mistaken."`}</em></p>
    <p><strong parentName="p">{`Codereviewer:`}</strong>{` `}<em parentName="p">{`"Would it? That doesn't seem very intuitive."`}</em></p>
    <p><strong parentName="p">{`Swiz:`}</strong>{` `}<em parentName="p">{`"Welcome to JavaScript's dynamic scoping. Where have you been the past few years?…`}</em></p>
    <p><em parentName="p">{`But you're right, it does seem to work. I think MobX's `}<inlineCode parentName="em">{`@action`}</inlineCode>{` does a bind for us. It definitely wouldn't work with vanilla functions. I'll change the code code."`}</em></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/11/3_hours_later.jpg",
        "alt": null
      }}></img></p>
    <p>{`Nope, no, nah. I should not have done that. That did not work. `}<inlineCode parentName="p">{`@action`}</inlineCode>{` does not do any binding. Wrapping a function in a function does not create a closure with the correct local scope.`}</p>
    <p>{`But it `}<em parentName="p">{`did`}</em>{` work in that one case I tried. Just not in the other. ?`}</p>
    <h2 {...{
      "id": "lets-investigate"
    }}>{`Let's investigate.`}</h2>
    <p>{`Here's a self-contained example in Codepen - a clicker.`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "http://codepen.io/swizec/pen/XNeENX/"
      }}>{`React, mobx, bind example`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "http://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "http://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <p>{`We have a `}<inlineCode parentName="p">{`Store`}</inlineCode>{` that holds the number of `}<inlineCode parentName="p">{`clicks`}</inlineCode>{` and contains an `}<inlineCode parentName="p">{`inc`}</inlineCode>{` action to increase the count.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Store {
  @observable clicks = 0;

  @action inc() {
    this.clicks += 1;
  }
}
`}</code></pre>
    <p>{`A not-very-smart functional stateless component renders the current count and a link to click. We use `}<inlineCode parentName="p">{`onClick`}</inlineCode>{` with a bounded action to detect clicks.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const Clicky = observer(({ store }) => (
  <div>
    Clicks: {store.clicks}
    <br>
    <a href="#" onclick={store.inc.bind(store)}>+1</a>
  </div>
));
`}</code></pre>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/11/clicker.gif",
        "alt": null
      }}></img></p>
    <p>{`Click the link, number goes up. 14 lines of actual code.`}</p>
    <p>{`Ok, we know it works with a `}<inlineCode parentName="p">{`.bind`}</inlineCode>{`. Will it work without one?`}</p>
    <p>{`See the Pen `}<a parentName="p" {...{
        "href": "http://codepen.io/swizec/pen/LbzdBO/"
      }}>{`React, mobx, bind example, pt2`}</a>{` by Swizec Teller (`}<a parentName="p" {...{
        "href": "http://codepen.io/swizec"
      }}>{`@swizec`}</a>{`) on `}<a parentName="p" {...{
        "href": "http://codepen.io"
      }}>{`CodePen`}</a>{`.`}</p>
    <p>{`We expanded our `}<inlineCode parentName="p">{`Clicky`}</inlineCode>{` component with a link that uses an unbounded `}<inlineCode parentName="p">{`store.inc`}</inlineCode>{` action call as the `}<inlineCode parentName="p">{`onClick`}</inlineCode>{` callback.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const Clicky = observer(({ store }) => (
  <div>
    Clicks: {store.clicks}
    <br>
    <a href="#" onclick={store.inc.bind(store)}>+1 bound</a>
    <br>
    <a href="#" onclick={store.inc}>+1 unbound</a>
  </div>
));
`}</code></pre>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/11/clicker2.gif",
        "alt": null
      }}></img></p>
    <p>{`Well, that didn't work. ?`}</p>
    <p>{`The error reads `}<inlineCode parentName="p">{`Uncaught TypeError: Cannot read property 'clicks' of undefined`}</inlineCode>{`. Looks like `}<inlineCode parentName="p">{`this`}</inlineCode>{` isn't defined inside our callback.`}</p>
    <p>{`Good ol' `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Scope_(computer_science)#Dynamic_scoping"
      }}>{`dynamic scoping`}</a>{`, unintuitive as always. If that Wikipedia article doesn't tell you much, don't worry. It's `}<em parentName="p">{`basically`}</em>{` comp sci soup.`}</p>
    <p>{`The gist of their 800-word explanation is this: with dynamic scoping, functions are scoped to where they're called, not where they're defined. It doesn't matter that `}<inlineCode parentName="p">{`inc`}</inlineCode>{` is defined inside `}<inlineCode parentName="p">{`Store`}</inlineCode>{`; it matters how React calls it.`}</p>
    <p>{`So how `}<em parentName="p">{`does`}</em>{` React call our click callback? Let's see.`}</p>
    <h2 {...{
      "id": "a-spelunk-through-a-react-event"
    }}>{`A spelunk through a React event`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/11/Screen-Shot-2016-11-29-at-00.29.37.png",
        "alt": null
      }}></img></p>
    <p>{`It starts with `}<inlineCode parentName="p">{`dispatchEvent`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`dispatchEvent: function (topLevelType, nativeEvent) {
    if (!ReactEventListener._enabled) {
      return;
    }

    var bookKeeping = TopLevelCallbackBookKeeping.getPooled(topLevelType, nativeEvent);
    try {
      // Event queue being processed in the same cycle allows
      // \`preventDefault\`.
      ReactUpdates.batchedUpdates(handleTopLevelImpl, bookKeeping);
    } finally {
      TopLevelCallbackBookKeeping.release(bookKeeping);
    }
  }
`}</code></pre>
    <p>{`Looks like a function that takes native events from the browser and emits them into React's engine in batches. That's good for performance, great for debugging, and makes everyone's lives easier.`}</p>
    <p>{`Our next step in the chain is `}<inlineCode parentName="p">{`batchedUpdates`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var transaction = new ReactDefaultBatchingStrategyTransaction();

var ReactDefaultBatchingStrategy = {
  isBatchingUpdates: false,

  /**
   * Call the provided function in a context within which calls to \`setState\`
   * and friends are batched such that components aren't updated unnecessarily.
   */
  batchedUpdates: function (callback, a, b, c, d, e) {
    var alreadyBatchingUpdates = ReactDefaultBatchingStrategy.isBatchingUpdates;

    ReactDefaultBatchingStrategy.isBatchingUpdates = true;

    // The code is written this way to avoid extra allocations
    if (alreadyBatchingUpdates) {
      callback(a, b, c, d, e);
    } else {
      transaction.perform(callback, null, a, b, c, d, e);
    }
  },
};
`}</code></pre>
    <p>{`Told ya it was good for performance. Just look at that comment – `}<em parentName="p">{`"components aren't updated unnecessarily"`}</em>{`.`}</p>
    <p>{`I have no idea how this thing works. It smells like some sort of `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Currying"
      }}>{`currying`}</a>{`.`}</p>
    <p>{`But we know our next step, eventually, is `}<inlineCode parentName="p">{`transaction.perform`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`perform: function (method, scope, a, b, c, d, e, f) {
    !!this.isInTransaction() ? "development" !== 'production' ? invariant(false, 'Transaction.perform(...): Cannot initialize a transaction when there is already an outstanding transaction.') : _prodInvariant('27') : void 0;
    var errorThrown;
    var ret;
    try {
      this._isInTransaction = true;
      // Catching errors makes debugging more difficult, so we start with
      // errorThrown set to true before setting it to false after calling
      // close -- if it's still set to true in the finally block, it means
      // one of these calls threw.
      errorThrown = true;
      this.initializeAll(0);
      ret = method.call(scope, a, b, c, d, e, f);
      errorThrown = false;
    } finally {
      try {
        if (errorThrown) {
          // If \`method\` throws, prefer to show that stack trace over any thrown
          // by invoking \`closeAll\`.
          try {
            this.closeAll(0);
          } catch (err) {}
        } else {
          // Since \`method\` didn't throw, we don't want to silence the exception
          // here.
          this.closeAll(0);
        }
      } finally {
        this._isInTransaction = false;
      }
    }
    return ret;
  },
`}</code></pre>
    <p>{`?`}</p>
    <p>{`That's a lot of code. Let's focus on the bits that matter:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`perform: function (method, scope, a, b, c, d, e, f) {
// ...
      ret = method.call(scope, a, b, c, d, e, f);
// ...
`}</code></pre>
    <p>{`A-ha! The second argument is `}<inlineCode parentName="p">{`scope`}</inlineCode>{`. React specifically set that to `}<inlineCode parentName="p">{`null`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`transaction.perform`}</inlineCode>{`, and `}<inlineCode parentName="p">{`function.call`}</inlineCode>{` is a JavaScript way to define a function's scope at point of invocation.`}</p>
    <blockquote>
      <p parentName="blockquote">{`The `}<inlineCode parentName="p">{`call()`}</inlineCode>{` method calls a function with a given `}<inlineCode parentName="p">{`this`}</inlineCode>{` value and arguments provided individually.`}</p>
    </blockquote>
    <p>{`I don't know `}<em parentName="p">{`why`}</em>{` React goes out of its way to set callback scope to `}<inlineCode parentName="p">{`null`}</inlineCode>{`, but I'm sure it has something to do with correctness. Better to throw an explicit error and tell the engineer to fix their code than to get the wrong `}<inlineCode parentName="p">{`this`}</inlineCode>{` by accident and fuck shit up.`}</p>
    <p>{`And I'm not sure when, if ever, that `}<inlineCode parentName="p">{`scope`}</inlineCode>{` argument would be something other than `}<inlineCode parentName="p">{`null`}</inlineCode>{`.`}</p>
    <p>{`¯`}{`_`}{`(ツ)`}{`_`}{`/¯`}</p>
    <h2 {...{
      "id": "what-did-we-learn"
    }}>{`What did we learn`}</h2>
    <p>{`Bind your callbacks.`}</p>
    <p>{`Add a `}<inlineCode parentName="p">{`.bind`}</inlineCode>{` or a fat arrow wrap -> `}<inlineCode parentName="p">{`() => callback()`}</inlineCode>{`. Both work.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      